<template>
    <div class="instrument-list">
      <div class="header">
      <h3>Instruments</h3>
      <button @click="navigateToCreateInstrument" class="new-instrument-button">
        New Instrument
      </button>
      </div>

  
      <div class="search-filter-row">
        <input type="text" v-model="searchQuery" placeholder="Search by Name..." class="search-input">
        <button @click="showFilters = !showFilters" class="toggle-filter-button">
          {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
        </button>
      </div>
  
      <!-- Collapsible Filter Menu -->
      <div v-if="showFilters" class="filters">
        <input type="date" v-model="startCalibration" class="date-input" placeholder="Start Calibration Time">
        <input type="date" v-model="endCalibration" class="date-input" placeholder="Next Calibration Time">
      </div>

      <table class="instrument-table">
        <thead>
          <tr>
            <th @click="sortInstruments('name')">Name<i v-if="currentSortColumn === 'name'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
            <th @click="sortInstruments('c_calibration_time')">Calibration Time<i v-if="currentSortColumn === 'c_calibration_time'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
            <th @click="sortInstruments('n_calibration_time')">Next Calibration<i v-if="currentSortColumn === 'n_calibration_time'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
            <th @click="sortInstruments('location_name')">Location<i v-if="currentSortColumn === 'location_name'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="instrument in paginatedInstruments" :key="instrument.id" @click="navigateToInstrumentDetail(instrument.id)" class="instrument-row">
            <td>{{ instrument.name }}</td>
            <td>{{ formatDate(instrument.c_calibration_time) }}</td>
            <td>{{ formatDate(instrument.n_calibration_time) }}</td>
            <td>{{ locationNames[instrument.location_id] || 'Loading...' }}</td> 
          </tr>
        </tbody>
      </table>
  
      <div class="pagination">
        <button v-for="page in visiblePageNumbers" :key="page" @click="setPage(page)" :class="{ active: page === currentPage }">{{ page }}</button>
      </div>
    </div>
</template>

<script>
import axios from 'axios';
import { API_ENDPOINTS } from '@/services/config.js'; 

export default {
  data() {
    return {
      instruments: [], 
      locationNames: {}, 
      searchQuery: '',
      currentPage: 1,
      instrumentsPerPage: 15,
      currentSortColumn: 'name',
      currentSortDirection: 'asc',
      showFilters: false,
      startCalibration: '',
      endCalibration: '',
      sortedInstruments: [] // Local copy for sorting
    };
  },
  created() {
    this.fetchInstruments(); // Fetch instruments on component creation
  },
  methods: {
    navigateToCreateInstrument() {
      this.$router.push({ path: '/create-instrument' });
    },
    async fetchInstruments() {
      try {
        const response = await axios.get(API_ENDPOINTS.allInstruments);
        this.instruments = response.data;
        this.sortedInstruments = [...this.instruments];
        this.sortInstruments(this.currentSortColumn);

        // Fetch location names
        await this.fetchLocationNames();
      } catch (error) {
        if (error.code != 404){
          console.error("Error fetching instruments:", error);
        }
      }
    },
    async fetchLocationNames() {
  // Get unique location IDs from instruments
  const uniqueLocationIds = [...new Set(this.instruments.map(instrument => instrument.location_id))];

  // Fetch each location name and cache it
  for (const locationId of uniqueLocationIds) {
    if (locationId && !this.locationNames[locationId]) {
      try {
        const response = await axios.get(`${API_ENDPOINTS.locations}/${locationId}`);
        this.locationNames[locationId] = response.data.name; // Set location name directly
      } catch (error) {
        console.error(`Error fetching location with ID ${locationId}:`, error);
      }
    }
  }
},
    sortInstruments(column) {
      if (this.currentSortColumn === column) {
        this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.currentSortDirection = 'asc';
        this.currentSortColumn = column;
      }
      this.sortedInstruments.sort((a, b) => {
        if (a[column] < b[column]) return this.currentSortDirection === 'asc' ? -1 : 1;
        if (a[column] > b[column]) return this.currentSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    },
    setPage(pageNumber) {
      this.currentPage = pageNumber;
    },
    setArrowDirection(sortDirection) {
      return sortDirection === 'desc' ? "bi bi-arrow-down" : "bi bi-arrow-up";
    },
    navigateToInstrumentDetail(instrumentId) {
      this.$router.push({ name: 'InstrumentDetailPage', params: { id: instrumentId } });
    },
    formatDate(dateString) {
        if (!dateString) {
        return "N/A";
        }
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
      },
  },
  computed: {
    filteredInstruments() {
      return this.sortedInstruments.filter(instrument => {
        const name = instrument.name?.toLowerCase() || '';
        const matchesSearchQuery = name.includes(this.searchQuery.toLowerCase());
        const matchesStartCalibration = this.startCalibration === '' || new Date(instrument.c_calibration_time) >= new Date(this.startCalibration);
        const matchesEndCalibration = this.endCalibration === '' || new Date(instrument.n_calibration_time) <= new Date(this.endCalibration);
        return matchesSearchQuery && matchesStartCalibration && matchesEndCalibration;
      });
    },
    paginatedInstruments() {
      const start = (this.currentPage - 1) * this.instrumentsPerPage;
      const end = start + this.instrumentsPerPage;
      return this.filteredInstruments.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredInstruments.length / this.instrumentsPerPage);
    },
    visiblePageNumbers() {
      const range = 15;
      let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
      let endPage = startPage + range - 1;
      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - range + 1);
      }
      return Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.instrument-list {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      color: $primary-color;
    }

    .new-instrument-button {
      padding: 8px 16px;
      background-color: $primary-color;
      color: #fff;
      border: none;
      border-radius: 5px;
      cursor: pointer;
    }

    .new-instrument-button:hover {
      background-color: darken($primary-color, 10%);
    }
  }

  .search-filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
  }

  .search-input {
    flex: 1;
    margin: 0;
  }

  .toggle-filter-button {
    width: auto;
    margin: 0;
    padding: 6px 10px;
    white-space: nowrap;
  }

  .instrument-table {
      width: 100%;
  }

  .instrument-table th{
    background-color: $form-color;
    border: 1px solid $primary-color;
    cursor: pointer;
  }

  .filters {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: none;
    margin: 6px 0;
    gap: 8px;
  }

  .instrument-row{
    background-color: $secondary-color;
    cursor: pointer;
  }

  .instrument-row:hover{
    background-color: $form-color;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }

  .pagination button {
    margin: 0 5px;
    padding: 5px 10px;
    background-color: $primary-color;
    border: 1px solid $secondary-color;
    border-radius: 5px;
    cursor: pointer;
  }

  .pagination button:hover,
  .pagination button.active {
    background-color: darken($primary-color, 10%);
  }
}
</style>
